import {defineStore} from "pinia";
import {useToast} from "vue-toastification";
import {
  isValidEmail,
  isValidNumber,
  isValidPassword,
  isValidPhone,
  isValidString,
  isValidURL,
  isValidUsername,
} from "../helpers/ValidationRules";
import {arrayToCamelCase, objectToCamelCase, objectToSnakeCase} from "../helpers/CaseTransformer";
import {validateField} from "../helpers/CommonAccountFunctions.js";
import {v4 as uuid} from "uuid";
import {getCookie, setCookie} from "../helpers/Cookies.js";
import {gtagNewCompany, gtagNewEnterprise} from "../helpers/GoogleAnalytics.js";
import {CYCLE_INTERVAL_YEAR} from "../constants/Billing.js";
import CompanyApi from "../api/CompanyApi";
import BillingPlanApi from "../api/BillingPlanApi.js";
import EnterpriseApi from "../api/EnterpriseApi";

const toast = useToast();

export const useCompanyAccountCreateStore = defineStore(
  "companyAccountCreate",
  {
    state: () => ({
      username: "",
      name: "",
      website: "",
      locationGroupName: "",
      locations: [
        {
          location: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          country: "",
          postalCode: "",
          lat: null,
          lng: null,
          nickname: "",
        },
      ],
      companyPhoneVerificationCode: "",
      tosChecked: false,
      enableDefaultSmsNotifications: true,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      passwordConfirmation: "",
      smsVerificationCode: "",
      existingEmail: "",
      existingPassword: "",
      useExistingAccountAsAdmin: false,
      billingEmail: "",
      billingLocation: "",
      billingAddressLine1: "",
      billingAddressLine2: "",
      billingCity: "",
      billingState: "",
      billingCountry: "",
      billingPostalCode: "",
      promotionCodeId: null,
      trialDays: 0,
      billingPlanId: null,
      stripePaymentId: "",
      cycleInterval: CYCLE_INTERVAL_YEAR,
      checkoutSessionUuid: "",
      referralCode: "",
      billingPlans: [],
      furthestStep: "",
      furthestStepAt: null,
      totalSteps: 4,
      completedSteps: 0,
      activatingTrial: false,
    }),
    actions: {
      setFurthestStep(number, name) {
        if (number <= this.completedSteps) return;
        this.furthestStep = name;
        this.furthestStepAt = new Date();
        this.completedSteps = number;
      },
      validateStep1() {
        return true;
      },
      async validateStep2(notifyUserOnError = false) {
        let nameValid = validateField(
          isValidString(this.name, true, 250),
          notifyUserOnError,
          "Company name is required and must be less than 250 characters",
          (error) => (this.errors.name = error),
          () => this.clearError("name")
        );

        let websiteValid = validateField(
          isValidURL(this.website, false),
          notifyUserOnError,
          "Please enter a valid company website",
          (error) => (this.errors.website = error),
          () => this.clearError("website")
        );

        let allLocationsAreValid = true;

        this.locations.forEach((location, index) => {
          const locationLabel = `Location #${index + 1}`;

          let locationValid = validateField(
            isValidString(location.location, true),
            notifyUserOnError,
            `${locationLabel} is missing an address`,
            (error) => (this.errors.location = error),
            () => this.clearError("location")
          );

          let latValid = validateField(
            isValidNumber(location.lat, -90, 90),
            notifyUserOnError,
            `${locationLabel} has an invalid address - please select an option from the dropdown`,
            (error) => (this.errors.lat = error),
            () => this.clearError("lat")
          );

          let lngValid = validateField(
            isValidNumber(location.lng, -180, 180),
            notifyUserOnError,
            `${locationLabel} has an invalid address - please select an option from the dropdown`,
            (error) => (this.errors.lng = error),
            () => this.clearError("lng")
          );

          let nicknameValid = true;

          if (this.locations.length > 1) {
            nicknameValid = validateField(
              isValidString(location.nickname, true),
              notifyUserOnError,
              `${locationLabel} is missing a name`,
              (error) => (this.errors.locations = error),
              () => this.clearError("locations")
            );
          }

          if (!locationValid || !latValid || !lngValid || !nicknameValid) {
            allLocationsAreValid = false;
          }
        });

        let locationGroupNameValid = true;

        if (this.locations.length > 1) {
          locationGroupNameValid = validateField(
            isValidString(this.locationGroupName, true),
            notifyUserOnError,
            "Please name the group of locations",
            (error) => (this.errors.locationGroupName = error),
            () => this.clearError("locationGroupName")
          );
        }

        this.setFurthestStep(1, "Tell us about your company");

        return (
          nameValid &&
          websiteValid &&
          allLocationsAreValid &&
          locationGroupNameValid &&
          this.tosChecked
        );
      },
      validateStep3() {
        return true;
      },
      async validateStep4(notifyUserOnError = false) {
        if (this.useExistingAccountAsAdmin) {
          return !!this.existingEmail && !!this.existingPassword;
        }

        let firstNameValid = validateField(
          isValidString(this.firstName, true),
          notifyUserOnError,
          "Company admin first name is required",
          (error) => (this.errors.firstName = error),
          () => this.clearError("firstName")
        );

        let lastNameValid = validateField(
          isValidString(this.lastName, true),
          notifyUserOnError,
          "Company admin last name is required",
          (error) => (this.errors.lastName = error),
          () => this.clearError("lastName")
        );

        let emailValid = validateField(
          isValidEmail(this.email, true),
          notifyUserOnError,
          "Please enter a valid email",
          (error) => (this.errors.email = error),
          () => this.clearError("email")
        );

        let phoneValid = validateField(
          isValidPhone(this.phone, true),
          notifyUserOnError,
          "Please enter a valid phone number",
          (error) => (this.errors.phone = error),
          () => this.clearError("phone")
        );

        let passwordValid = validateField(
          isValidPassword(this.password, true),
          notifyUserOnError,
          "Password must be at least 8 characters and contain at least one number and one letter",
          (error) => (this.errors.password = error),
          () => this.clearError("password")
        );

        let passwordsMatch = validateField(
          this.password === this.passwordConfirmation,
          notifyUserOnError,
          "Passwords must match",
          (error) => (this.errors.passwordConfirmation = error),
          () => this.clearError("passwordConfirmation")
        );

        let usernameValid = validateField(
          isValidUsername(this.username, true),
          notifyUserOnError,
          "Username must be between 5-30 characters and contain only letters, numbers, and periods. It must start with a letter, and have no consecutive periods.",
          (error) => (this.errors.username = error),
          () => this.clearError("username")
        );

        this.setFurthestStep(2, "Setup a company admin account");

        return (
          firstNameValid &&
          lastNameValid &&
          emailValid &&
          phoneValid &&
          passwordValid &&
          passwordsMatch &&
          usernameValid
        );
      },
      validateStep5() {
        return true;
      },
      validateStep7(notifyUserOnError = false) {
        // validate billing email
        let billingEmailValid = validateField(
          isValidEmail(this.billingEmail, true),
          notifyUserOnError,
          "Please enter a valid email",
          (error) => (this.errors.billingEmail = error),
          () => this.clearError("billingEmail")
        );

        let billingAddressLine1 = validateField(
          isValidString(this.billingAddressLine1, true),
          notifyUserOnError,
          "Please check the billing address",
          (error) => (this.errors.billingAddressLine1 = error),
          () => this.clearError("billingAddressLine1")
        );

        let billingAddressLine2 = validateField(
          isValidString(this.billingAddressLine2, false),
          notifyUserOnError,
          "Please check the billing address",
          (error) => (this.errors.billingAddressLine2 = error),
          () => this.clearError("billingAddressLine2")
        );

        let cityValid = validateField(
          isValidString(this.billingCity, true),
          notifyUserOnError,
          "Please check the billing address",
          (error) => (this.errors.billingCity = error),
          () => this.clearError("billingCity")
        );

        let stateValid = validateField(
          isValidString(this.billingState, true),
          notifyUserOnError,
          "Please check the billing address",
          (error) => (this.errors.billingState = error),
          () => this.clearError("billingState")
        );

        let countryValid = validateField(
          isValidString(this.billingCountry, true),
          notifyUserOnError,
          "Please check the billing address",
          (error) => (this.errors.billingCountry = error),
          () => this.clearError("billingCountry")
        );

        let postalCodeValid = validateField(
          isValidString(this.billingPostalCode, true),
          notifyUserOnError,
          "Please check the billing address",
          (error) => (this.errors.billingPostalCode = error),
          () => this.clearError("billingPostalCode")
        );

        let referrerCodeValid = validateField(
          isValidString(this.referrerCode, false),
          notifyUserOnError,
          "Please enter a valid referrer code",
          (error) => (this.errors.referrerCode = error),
          () => this.clearError("referrerCode")
        );

        return billingEmailValid && billingAddressLine1 && billingAddressLine2 && cityValid && stateValid && countryValid && postalCodeValid && referrerCodeValid;
      },
      addLocation() {
        this.locations.push({
          location: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          country: "",
          postalCode: "",
          lat: null,
          lng: null,
        });
      },
      clearError(fieldName) {
        if (!this.errors) this.errors = {};
        if (!(fieldName in this.errors)) return;
        this.errors[fieldName] = "";
      },
      async getBillingPlans() {
        const api = new BillingPlanApi();

        try {
          const response = await api.index();
          this.billingPlans = arrayToCamelCase(response.data);
        } catch (error) {
          toast.error("Failed to load billing plans");
          console.error(error);
        }
      },
      async createCompany(callback) {
        if (this.locations.length > 1) await this.createMultiLocationCompany(callback);
        else await this.createSingleLocationCompany(callback);
      },
      async createMultiLocationCompany(callback) {
        window.progressBar.start();

        const navigatorLanguage = navigator.language || navigator.userLanguage;

        const api = new EnterpriseApi();

        await api
          .create({
            ...objectToSnakeCase(this.$state, ["billingPlans"]),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigatorLanguage,
          })
          .then((res) => {
            if (!res.data.id) toast.warning("Company sign up is not enabled at this time. Please try again later.");
            if (res.data.id) {
              window.progressBar.complete();
              gtagNewEnterprise(res.data.id);
              callback(true);
              toast.success("Welcome to Business Draft, " + this.name + "!");
            }
          })
          .catch((err) => callback(false, err))
          .finally(() => window.progressBar.complete());
      },
      async createSingleLocationCompany(callback) {
        window.progressBar.start();

        const api = new CompanyApi();

        const data = {
          ...objectToSnakeCase(this.$state, ["billingPlans", "locations", "locationGroupName"]), // We don't need group names, other locations
          ...objectToSnakeCase(this.locations[0]), // We only want the first location
        };

        await api
          .create({
            ...data,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language || navigator.userLanguage,
          })
          .then((res) => {
            if (!res.data.id) toast.warning("Company sign up is not enabled at this time. Please try again later.");
            if (res.data.id) {
              window.progressBar.complete();
              gtagNewCompany(res.data.id);
              callback(true);
              toast.success("Welcome to Business Draft, " + this.name + "!");
            }
          })
          .catch((err) => callback(false, err))
          .finally(() => window.progressBar.complete());
      },
      handleCheckoutUuid() {
        let checkoutSessionUuidCookie = getCookie("checkoutSessionUuid");
        if (!checkoutSessionUuidCookie) {
          checkoutSessionUuidCookie = uuid() + uuid();
          setCookie("checkoutSessionUuid", checkoutSessionUuidCookie, 1);  // Expires in 1 day
        }
        this.checkoutSessionUuid = checkoutSessionUuidCookie;
      },
      async handleActivateTrial(token) {
        if (!token) return;

        const api = new CompanyApi();
        this.activatingTrial = true;

        try {
          const response = await api.activateTrial(token);
          Object.assign(this.$state, objectToCamelCase(response.data));
        } finally {
          this.activatingTrial = false;
        }
      }
    },
  }
);
